<template>
  <div
    class="contant d-flex flex-column align-center"
    :class="{ mobile: isMobile }"
  >
    <div class="d-flex flex-column">
      <div class="left">
        <a href="/" class="vouchLogo-link">
          <img src="@/assets/vouch_logo.png" class="vouchLogo" alt="Vouch logo" />
        </a>

        <h1 class="des mt-7">
          Simplify Trust with Verifiable Credentials and Decentralized Identity.
        </h1>

        <div class="download mt-10">
          <div class="d-flex flex-row flex-wrap align-center justify-center">
            <a
              class="startBtn"
              href="https://vouchweb.uptick.network/"
              target="_blank"
              rel="noopener noreferrer"
              >Get Started ></a
            >
          </div>
        </div>
        <div class="upwardDes1">
          <img src="@/assets/image01.png" class="uptickImage" alt="Vouch verifiable credentials and decentralized identity platform" />
        </div>

        <h2 class="title-25">Why Vouch?</h2>
        <div class="customDes mt-9">
          Vouch is an all-in-one platform for issuing verifiable credentials
          (VCs) and decentralized identifiers (DIDs), covering everything from
          DID creation to selecting credential templates, designing credentials,
          and managing issuance. Its intuitive interface brings everything into
          one place, and it connects directly with the Upward Wallet app for
          easy access and management.
        </div>
      </div>
      <div class="vouchImage">
        <img src="@/assets/image02.png" class="createImage" alt="Create and issue verifiable credentials with Vouch" />
      </div>
    </div>

    <h2 class="title-25 mt-10">Core Capabilities</h2>

    <div class="upwardDes">
      <div class="footerInfo highLight">
        <div class="hightItem">
          <div
            class="item"
            v-for="(item, index) in hightLightList"
            :key="index"
          >
            <img
              :src="item.image"
              class="iconImage"
              :alt="item.name"
            />
            <div class="itemRight ml-7">
              <h3 class="name">{{ item.name }}</h3>
              <div class="description mt-4">{{ item.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h2 class="title-25 mt-10">Frequently Asked Questions</h2>
    <div class="faqList mt-9">
      <div class="faqItem" v-for="(faq, i) in faqList" :key="'faq' + i">
        <h3
          class="faqQuestion"
          :class="{ open: openFaqIndex === i }"
          @click="openFaqIndex = openFaqIndex === i ? -1 : i"
        >
          <span class="faqQuestionText">{{ faq.q }}</span>
        </h3>
        <div class="faqAnswerWrap" v-show="openFaqIndex === i">
          <p class="faqAnswer">
            {{ faq.a }}
            <a class="faqLink" :href="faq.link" target="_blank" rel="noopener noreferrer">Read the full guide &rarr;</a>
          </p>
        </div>
      </div>
    </div>

    <h2 class="title-25 mt-10">Vouch Help Center</h2>
    <p class="customDes mt-5">Step-by-step guides to get the most out of Vouch.</p>
    <div class="helpGrid mt-9">
      <a class="helpCard" v-for="(h, i) in helpList" :key="'help' + i" :href="h.link" target="_blank" rel="noopener noreferrer">
        <span class="helpTitle">{{ h.title }}</span>
        <span class="helpDesc">{{ h.desc }}</span>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home',
  components: {},
  data: function () {
    return {
      openFaqIndex: -1,

      hightLightList: [
        {
          image: require('@/assets/image03.png'),
          name: 'Privacy Protection & Data Control',
          description:
            'Users decide what to share, keeping their data private and preserving its authenticity.',
        },

        {
          image: require('@/assets/image06.png'),
          name: 'Enhanced Trust & Security',
          description:
            'DIDs and VCs strengthen identity security and trust with encryption and decentralization.',
        },
        {
          image: require('@/assets/image04.png'),
          name: 'Convenience & Efficiency',
          description:
            'Easily create a wallet with familiar social login options.',
        },
        {
          image: require('@/assets/image07.png'),
          name: 'Optimized User Experience',
          description:
            'An intuitive interface that simplifies the creation and management of digital identities and credentials.',
        },
        {
          image: require('@/assets/image05.png'),
          name: 'Unlock Value Across Industries',
          description:
            'Integrate Web3 with traditional systems to strengthen existing products without disrupting business models.',
        },
        {
          image: require('@/assets/image08.png'),
          name: 'Cross-Platform Interoperability',
          description:
            'Achieve broad compatibility across platforms and industries, adapting to diverse system needs.',
        },
      ],
      faqList: [
        {
          q: 'What is Vouch?',
          a: 'Vouch is an all-in-one platform by Uptick Network that lets you issue, manage, and verify verifiable credentials (VCs) and decentralized identifiers (DIDs) — no advanced Web3 knowledge required. Just sign up with an email to get started.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-registrate-a-vouch-account',
        },
        {
          q: 'How do I create a DID with Vouch?',
          a: 'After registering your account, open the left-side menu and provide a logo, public name, and description to create your DID — a unique digital identifier for your organization. You can create multiple DIDs for different use cases.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-create-did',
        },
        {
          q: 'How do I issue a verifiable credential?',
          a: 'Pick a credential schema such as Business Card or Competition Certificate, customize the design, add recipients via Direct Issue or a shareable Credential Link with QR code, then send. Issued credentials are recorded on the Uptick Network chain.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-issue-credentials',
        },
        {
          q: "How do I verify someone's credential?",
          a: 'Create a verification template, then the holder scans the request QR code with the Upward Wallet app and selects their DID — the system returns "Verification Passed" or "Verification Failed" instantly.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-verify-credentials',
        },
        {
          q: 'How do I collect and store my credentials as a holder?',
          a: 'Download the Upward Wallet app, create a Web3 wallet and your DID, then share your DID with an issuer to receive credentials. All your verifiable credentials are browsable in the Credentials section.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-collect-credentials',
        },
      ],
      helpList: [
        {
          title: 'How to registrate a Vouch account?',
          desc: 'Sign up with just an email — no Web3 expertise needed.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-registrate-a-vouch-account',
        },
        {
          title: 'How to create DID?',
          desc: 'Set up your unique decentralized identifier in minutes.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-create-did',
        },
        {
          title: 'How to issue credentials?',
          desc: 'Design and send VCs via Direct Issue or Credential Link.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-issue-credentials',
        },
        {
          title: 'How to management credentials?',
          desc: 'Revoke, share, or deactivate issued credentials.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-management-credentials',
        },
        {
          title: 'How to collect credentials?',
          desc: 'Receive credentials with the Upward Wallet app.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-collect-credentials',
        },
        {
          title: 'How to browse credentials?',
          desc: 'View all your verifiable credentials in one place.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-browse-credentials',
        },
        {
          title: 'How to create verification template?',
          desc: 'Build reusable verification requests with QR codes.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-create-verification-template',
        },
        {
          title: 'How to sync the verification template to Upward app?',
          desc: 'Use the Upward App as your on-site verifier.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-sync-the-verification-template-to-upward-app',
        },
        {
          title: 'How to verify credentials?',
          desc: 'Scan, match, and get an instant pass/fail result.',
          link: 'https://upticknft.gitbook.io/vouch-help-center/how-to-verify-credentials',
        },
      ],
    };
  },
  computed: {
    isMobile: function () {
      return this.$vuetify.breakpoint.mobile;
    },
  },

};
</script>

<style lang="scss" scoped>
.contant {
  width: 100%;
  height: 100%;
  .startBtn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 270px;
    height: 61px;
    background-color: #000000;
    border-radius: 30px;
    font-family: AvenirNext-DemiBold;
    font-size: 25px;
    font-weight: normal;
    font-stretch: normal;
    line-height: 30px;
    letter-spacing: 1px;
    color: #ffffff;
    text-decoration: none;
  }
  .vouchLogo-link {
    display: inline-block;
    line-height: 0;
  }
  .title-25 {
    font-family: AvenirNext-Bold;
    font-size: 35px;
    font-weight: bold;
    font-stretch: normal;
    line-height: 40px;
    color: #000000;
    text-align: center;
  }
  .title-25-left {
    font-family: Helvetica;
    font-size: 25px;
    font-weight: bold;
    font-stretch: normal;
    line-height: 32px;
    color: #d4b07b;
    text-align: left;
  }
  .title-20 {
    font-family: Helvetica;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    line-height: 25px;
    color: #d4b07b;
  }
  .title-16 {
    max-width: 750px;
    font-family: Helvetica;
    font-size: 16px;
    font-weight: normal;
    font-stretch: normal;
    line-height: 25px;
    color: #d4b07b;
    span {
      font-weight: bold;
    }
  }
  .title-18 {
    max-width: 750px;
    font-family: Helvetica;
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    line-height: 30px;
    color: #d4b07b;
    span {
      font-weight: bold;
    }
  }

  .highLight {
    margin: 65px 0px 25px !important;
    background-color: #ffffff;
    box-shadow: 0px 4px 21px 2px rgba(0, 0, 102, 0.36);
    border-radius: 34px;
    padding: 50px 50px 0;
  }
  .footerInfo {
    margin: 110px 0px 25px;
    border-radius: 50px;
  }

  .left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 100px;
    .vouchLogo {
      height: 165px;
    }
    .des {
      width: 70%;
      font-family: AvenirNext-Bold;
      font-size: 30px;
      font-weight: bold;
      font-stretch: normal;
      line-height: 40px;
      color: #000000;
      text-align: center;
    }
    .create {
      width: 100%;
      font-family: Helvetica;
      font-size: 17px;
      font-weight: normal;
      font-stretch: normal;
      line-height: 30px;
      letter-spacing: 0px;
      color: #d4b07b;
      text-align: center;
    }
    .customDes {
      width: 80%;
      font-family: AvenirNext-Medium;
      font-size: 18px;
      font-weight: normal;
      font-stretch: normal;
      line-height: 25px;
      color: #000000;
      text-align: center;
    }

    .download {
      position: relative;
      margin-bottom: 20px;

      img {
        cursor: pointer;
      }
    }
    .description {
      width: 487px;
      font-family: Helvetica;
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      line-height: 15px;
      letter-spacing: 1px;
      color: #d4b07b;
      font-style: italic;
    }
  }

  .title-35 {
    font-family: Helvetica-Bold;
    font-size: 35px;
    font-weight: normal;
    font-stretch: normal;
    line-height: 40px;
    letter-spacing: 0px;
    color: #ffffff;
  }
  .hightItem {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .item {
      display: flex;
      flex-direction: row;
      width: 46%;
      margin-right: 8%;
      margin-bottom: 50px;
      &:nth-child(2n) {
        margin-right: 0;
      }
      .iconImage {
        height: 110px;
      }
      .name {
        margin: 0;
        font-family: AvenirNext-Bold;
        font-size: 18px;
        font-weight: normal;
        font-stretch: normal;
        line-height: 18px;
        letter-spacing: 0px;
        color: #000000;
      }
      .description {
        font-family: AvenirNext-Medium;
        font-size: 18px;
        font-weight: normal;
        font-stretch: normal;
        line-height: 25px;
        letter-spacing: 0px;
        color: #000000;
      }
    }
  }
  .upwardDes {
    width: 100%;

    .title-18 {
      width: 100%;
      font-family: Helvetica;
      font-size: 18px;
      font-weight: normal;
      font-stretch: normal;
      line-height: 28px;
      letter-spacing: 0px;
      color: #ffffff;
    }
    .uptickImage {
      width: 100%;
    }
  }
  .vouchImage {
    width: 100%;
    display: flex;
    justify-content: center;
    .createImage {
      width: 60%;
      margin-top: 44px;
    }
  }

  .faqList {
    width: 80%;
    max-width: 900px;
    .faqItem {
      text-align: left;
      margin-bottom: 0;
      padding: 14px 0;
      border-bottom: 1px solid rgba(0, 0, 102, 0.12);
      &:last-child {
        border-bottom: none;
        padding-bottom: 4px;
      }
    }
    .faqQuestion {
      margin: 0;
      padding: 0;
      font-family: AvenirNext-Medium;
      font-size: 16px;
      font-weight: normal;
      line-height: 22px;
      color: #000000;
      cursor: pointer;
      user-select: none;
      transition: color 0.2s ease;
      &:hover {
        color: #000000;
      }
    }
    .faqAnswerWrap {
      padding-top: 10px;
    }
    .faqAnswer {
      margin: 0;
      font-family: AvenirNext-Medium;
      font-size: 16px;
      line-height: 24px;
      color: #333333;
    }
    .faqLink {
      color: #000000;
      font-weight: bold;
      text-decoration: none;
      white-space: nowrap;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .helpGrid {
    width: 90%;
    max-width: 1000px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    .helpCard {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 22px 20px;
      background-color: #ffffff;
      box-shadow: 0px 4px 21px 2px rgba(0, 0, 102, 0.18);
      border-radius: 18px;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0px 8px 26px 4px rgba(0, 0, 102, 0.28);
      }
      .helpTitle {
        font-family: AvenirNext-Bold;
        font-size: 16px;
        font-weight: bold;
        line-height: 22px;
        color: #000000;
        margin-bottom: 8px;
      }
      .helpDesc {
        font-family: AvenirNext-Medium;
        font-size: 14px;
        line-height: 20px;
        color: #555555;
      }
    }
  }

  .upwardDes1 {
    width: 100%;
    margin-bottom: 80px;

    .title-18 {
      width: 100%;
      font-family: Helvetica;
      font-size: 18px;
      font-weight: normal;
      font-stretch: normal;
      line-height: 28px;
      letter-spacing: 0px;
      color: #ffffff;
    }
    .uptickImage {
      width: 100%;
    }
  }

  &.mobile {
    display: flex !important;
    flex-direction: column !important;
    margin-top: 90px;

    .Supported {
      margin-left: 5%;
    }
    .uptickImage {
      width: 90%;
      margin: 50px 0;
    }
    .footerInfo {
      width: 100%;
      padding: 25px;
    }
    .hightItem {
      .item {
        display: flex;
        flex-direction: row;
        width: 100%;
        margin-right: 0;
        margin-bottom: 30px;
        margin-top: 20px;
        .iconImage {
          height: 80px !important;
        }
      }
    }
    .vouchImage .createImage {
      width: 80%;
    }
    .upwardDes1 {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 20px;
    }
    .upwardDes {
      width: 90%;
    }
    .left {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 0px;
      .vouchLogo {
        height: 105px;
      }

      .des {
        width: 90%;
        text-align: center;
      }
      .description {
        width: 90%;
        text-align: center;
      }
      .testflight {
        margin-top: 8px;
        margin-bottom: 8px;
        margin-left: 0px !important;
      }
      .customDes {
        width: 90%;
      }

      .download {
        display: flex !important;
        flex-direction: column !important;
        align-items: center;
        width: 100%;
        position: relative !important;
      }
      .createImage {
        width: 90%;
      }
    }
    .helpGrid {
      grid-template-columns: 1fr;
      width: 92%;
    }
    .faqList {
      width: 92%;
    }
  }
}
</style>
